/**
 * DeviceCore — Main entry for the device domain.
 *
 * Aggregates `IdentityStore` + `Enroller` + signer helpers into a single
 * surface that the CLI / Extension / Bridge can call. Pure orchestration
 * — no HTTP of its own (the actual `POST /api/v1/devices/enroll` lives
 * behind `EnrollerOptions.enrollRequest`, wired in Phase 5.4).
 *
 * Refs:
 * - 4.功能规划.md §2.2 — `DeviceCore.ts 主入口`
 * - ADL-003 — data model in `@serviceme/devtools-protocol`
 * - `docs/architecture/phase-5-device-header-spec.md` §3.1
 */

import type {
	DeviceEnrollResult,
	DeviceIdentityState,
	DeviceMetadata,
	DeviceRotateSecretResult,
	DeviceStatus,
} from "@serviceme/devtools-protocol";
import { buildSignedHeaders, DeviceAuthHeaders, type DeviceSignedHeaders } from "./deviceAuth";
import { Enroller, type EnrollRequestFn } from "./Enroller";
import { IdentityStore } from "./IdentityStore";
import { deriveInstallationId } from "./InstallationId";
import type { PersistedDeviceIdentity } from "./types";

export interface DeviceCoreOptions {
	identityStore?: IdentityStore;
	enrollRequest?: EnrollRequestFn;
	now?: () => Date;
	/** Optional override for `deriveInstallationId` (used by tests for determinism). */
	resolveInstallationId?: () => string;
}

export class DeviceCore {
	private readonly identity: IdentityStore;
	private readonly enroller: Enroller;
	private readonly resolveInstallationId: () => string;

	constructor(opts: DeviceCoreOptions = {}) {
		this.identity = opts.identityStore ?? new IdentityStore();
		this.enroller = new Enroller({
			identityStore: this.identity,
			enrollRequest: opts.enrollRequest,
			now: opts.now,
		});
		this.resolveInstallationId = opts.resolveInstallationId ?? deriveInstallationId;
	}

	/** Read-only snapshot of the device status (matches `device.status` wire shape). */
	async status(): Promise<DeviceStatus> {
		const stored = await this.identity.read();
		return stored
			? {
					bindingState: stored.bindingState,
					identity: projectIdentity(stored),
					metadata: projectMetadata(stored),
					lastSyncAt: stored.lastSyncAt,
					lastSyncError: stored.lastSyncError,
				}
			: { bindingState: "anonymous" };
	}

	/** Enroll (or re-enroll) the device. */
	async enroll(opts: { force?: boolean; requireAuth?: boolean } = {}): Promise<DeviceEnrollResult> {
		return this.enroller.enroll(opts);
	}

	/** Wait for any in-flight enrollment to finish.  Use before `buildSignedHeaders` so that a concurrent `syncDeviceInfo` enrollment has time to write the identity to the store. */
	async waitForEnrollment(): Promise<void> {
		await this.enroller.waitForEnrollment();
	}

	/** True when an enrollment is currently in-flight. Used by callers to skip triggering a competing enrollment. */
	isEnrolling(): boolean {
		return this.enroller.isEnrolling();
	}

	/** Rotate the HMAC secret while keeping the previous one for the grace window. */
	async rotateSecret(opts: { gracePeriodDays?: number } = {}): Promise<DeviceRotateSecretResult> {
		const result = await this.enroller.rotateSecret(opts);
		const stored = await this.identity.read();
		return {
			...result,
			gracePeriodEndsAt: stored?.previousSecretExpiresAt,
		};
	}

	/** Build the canonical 5-header map for an outbound signed request. */
	async buildSignedHeaders(input: {
		method: string;
		path: string;
		body: string;
	}): Promise<DeviceSignedHeaders | null> {
		const stored = await this.identity.read();
		if (!stored) return null;
		return buildSignedHeaders({
			method: input.method,
			path: input.path,
			body: input.body,
			publicId: stored.publicId,
			deviceSecret: stored.deviceSecret,
			secretVersion: stored.secretVersion,
		});
	}

	/** Raw stored identity (CLI/extension internal use). Test seam too. */
	async readIdentity(): Promise<PersistedDeviceIdentity | null> {
		return this.identity.read();
	}

	/** Wipe the local identity (the `--force` path before re-enroll). */
	async clear(): Promise<void> {
		await this.identity.clear();
	}

	/** Mark the device as claimed (called by the bridge after a successful claim). */
	async markClaimed(): Promise<void> {
		await this.enroller.markClaimed();
	}

	/** Mark the device as expired (server returned an expiry response). */
	async markExpired(): Promise<void> {
		await this.enroller.markExpired();
	}

	/** Expose the identity store (CLI uses it for direct file access in tests). */
	getIdentityStore(): IdentityStore {
		return this.identity;
	}

	/** Expose the enroller (CLI uses it for state inspection). */
	getEnroller(): Enroller {
		return this.enroller;
	}

	/** Header name constants — re-exported from `deviceAuth.ts`. */
	getHeaderNames(): typeof DeviceAuthHeaders {
		return DeviceAuthHeaders;
	}

	/** Compute the installation id for the current machine. */
	getInstallationId(): string {
		return this.resolveInstallationId();
	}
}

function projectIdentity(stored: PersistedDeviceIdentity): DeviceIdentityState {
	return {
		publicId: stored.publicId,
		secretVersion: stored.secretVersion,
		bindingState: stored.bindingState,
	};
}

function projectMetadata(stored: PersistedDeviceIdentity): DeviceMetadata {
	return {
		installationId: stored.installationId,
		machineId: stored.machineId,
		platform: stored.platform,
		hostname: stored.hostname,
	};
}
